import { Rank, type EnemyDef } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   EB-2-g/d/h — LES TROIS PODS DU DONJON.

   Un fichier pour les trois : ce qui compte est la RÉPARTITION SPATIALE de la
   menace — gauche, droite, sommet — car c'est elle qui fait de chaque
   destruction l'ouverture d'un passage précis. Séparés, on perdrait de vue que
   les trois se partagent le terrain.

   ── La carte que composent leurs positions ──

     pod    place        ce que sa destruction rouvre
     ──────────────────────────────────────────────────────────────────────
     g      flanc gauche  la moitié gauche du terrain sous le Donjon.
     d      flanc droit   la moitié droite, symétriquement.
     h      sommet        le volume ambiant — l'anneau qui remplit l'espace
                          sans viser, et qu'on retire quand on veut respirer
                          plutôt que passer.

   Le noyau n'étant pas verrouillé, ces trois lignes ne sont pas un ordre imposé
   mais une carte de démolition : on lit où l'on veut aller, on ouvre le pod qui
   s'y oppose. 200 PV chacun — assez pour que l'ouverture se mérite, assez peu
   pour qu'elle reste plus rapide que de contourner toute la structure.
   ═══════════════════════════════════════════════════════════════════════════ */

const BASE = {
  rank: Rank.Standard,
  hp: 200,
  visualRadius: 16,
  score: 180,
  telegraph: 1.1,
  drops: 1,
  // Repositionné chaque tick sur son porteur : sa trajectoire propre n'est
  // jamais lue.
  motion: { kind: Motion.Anchor, speed: 0, holdY: 330 },
} as const;

const pods: EnemyDef[] = [
  defineEnemy({
    ...BASE,
    id: 'donjon-pod-g',
    emitter: {
      // Éventail statique penché : il condamne le flanc gauche.
      pattern: Pattern.Fan,
      every: 2.0,
      count: 4,
      spreadDeg: 44,
      speed: 360,
      firstDelay: 1.8,
      family: Fam.EnemyPurple,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'donjon-pod-d',
    emitter: {
      // Le miroir du gauche, décalé d'une demi-seconde pour que les deux flancs
      // ne pulsent pas à l'unisson.
      pattern: Pattern.Fan,
      every: 2.0,
      count: 4,
      spreadDeg: 44,
      speed: 360,
      firstDelay: 2.3,
      family: Fam.EnemyPurple,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'donjon-pod-h',
    emitter: {
      // L'anneau du sommet : le volume, la pulsation qui donne au Donjon sa
      // silhouette vivante. `stepDeg` décale chaque anneau pour qu'aucune place
      // sûre ne soit mémorisable d'un cycle à l'autre.
      pattern: Pattern.Ring,
      every: 2.2,
      count: 10,
      spreadDeg: 0,
      speed: 300,
      stepDeg: 11,
      firstDelay: 2.6,
      family: Fam.EnemyRed,
    },
  }),
];

export default pods;
